/**
 * useAnnotationMarkers
 * Projiziert 3D-Annotationspositionen in 2D-Bildschirmkoordinaten.
 *
 * WICHTIG: Verwendet direkte DOM-Manipulation (style.transform) statt
 * React-State, um die Marker absolut synchron mit der Kamerabewegung
 * zu halten — kein Frame-Lag durch React-Render-Pipeline.
 *
 * Occlusion-Dimming:
 * Alle ~200 ms wird per viewer.pick() geprüft, ob Geometrie zwischen
 * Kamera und Marker liegt. Verdeckte Marker werden halbtransparent
 * dargestellt, damit sie visuell in den Hintergrund treten.
 * Die Opacity wird per CSS-transition sanft animiert.
 */

import { useEffect, useRef, useCallback } from 'react'
import { Annotation } from '../types/annotations'

interface UseAnnotationMarkersOptions {
  annotations: Annotation[]
  viewerRef: React.RefObject<any>
  currentCloudId: string | null
  /** Ref auf den Container-<div>, der alle Marker-Elemente enthält */
  containerRef: React.RefObject<HTMLDivElement | null>
}

/** Wird einmal pro Annotation an AnnotationMarker übergeben (statisch) */
export interface MarkerPosition {
  id: string
  x: number
  y: number
  hidden: boolean
}

// ─── Occlusion-Konfig ──────────────────────────────────────────────────────
const OCCLUSION_INTERVAL_MS = 200   // Wie oft geprüft wird (ms)
const OCCLUDED_OPACITY      = '0.25' // Opacity für verdeckte Marker
const VISIBLE_OPACITY       = '1'
const DEPTH_TOLERANCE        = 0.97  // Marker gilt als verdeckt wenn pick < 97% seiner Distanz

export function useAnnotationMarkers({
  annotations,
  viewerRef,
  currentCloudId,
  containerRef,
}: UseAnnotationMarkersOptions) {
  const rafRef = useRef<number | null>(null)

  // Cache: Map<annotationId, HTMLElement> — wird bei DOM-Änderungen aktualisiert
  const elCacheRef = useRef<Map<string, HTMLElement>>(new Map())
  // Separater Cache für Dot-Kreise (nur der Punkt wird gedimmt, nicht das Label)
  const dotCacheRef = useRef<Map<string, HTMLElement>>(new Map())

  // Annotation-Daten als Ref, damit der rAF-Callback nie stale ist
  const annotationsRef = useRef(annotations)
  annotationsRef.current = annotations

  const cloudIdRef = useRef(currentCloudId)
  cloudIdRef.current = currentCloudId

  // Occlusion-State: welche IDs sind aktuell verdeckt
  const occludedRef = useRef<Set<string>>(new Set())

  /** Element-Cache neu aufbauen (nur IDs, die wir brauchen) */
  const rebuildCache = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const next = new Map<string, HTMLElement>()
    const dots = new Map<string, HTMLElement>()
    const els = container.querySelectorAll<HTMLElement>('[data-annotation-id]')
    els.forEach(el => {
      const id = el.getAttribute('data-annotation-id')
      if (id) {
        next.set(id, el)
        // Dot-Kreis für Occlusion-Dimming (nur der Punkt, nicht das Label)
        const dot = el.querySelector<HTMLElement>('[data-element="dot-circle"]')
        if (dot) dots.set(id, dot)
      }
    })
    elCacheRef.current = next
    dotCacheRef.current = dots
  }, [containerRef])

  // ─── Haupt-rAF-Loop: Position + Visibility (jeden Frame) ────────────────
  useEffect(() => {
    let running = true

    function loop() {
      if (!running) return

      const viewer = viewerRef.current
      const THREE = (window as any).Potree?.THREE
      if (!viewer || !THREE) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      if (!viewer.renderer?.domElement || !viewer.scene?.getActiveCamera) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      const camera = viewer.scene.getActiveCamera()
      if (!camera) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      // Kamera-Matrizen manuell aktualisieren, damit die Projektion
      // exakt zum aktuellen Frame passt.
      camera.updateMatrixWorld(true)

      const canvas: HTMLCanvasElement = viewer.renderer.domElement
      const w = canvas.clientWidth
      const h = canvas.clientHeight

      const anns = annotationsRef.current
      const cloudId = cloudIdRef.current
      const cache = elCacheRef.current
      const occluded = occludedRef.current

      // Clip-Box-Check: globale Funktion aus script.js (wird dort alle 150ms gecacht)
      const clipCheck = (window as any)._isInsideClipBox as
        ((pt: any) => boolean) | undefined

      for (const ann of anns) {
        if (cloudId && ann.cloudId !== cloudId) continue

        const el = cache.get(ann.id)
        if (!el) continue

        // Clip-Box-Filter: Annotation außerhalb der Box → ausblenden
        if (clipCheck) {
          try {
            // _isInsideClipBox liest .x/.y/.z direkt — ann.position reicht
            if (!clipCheck(ann.position)) {
              el.style.visibility = 'hidden'
              el.dataset.markerHidden = 'true'
              continue
            }
          } catch {
            // Fehler → kein Filter anwenden
          }
        }

        const vec = new THREE.Vector3(ann.position.x, ann.position.y, ann.position.z)
        vec.project(camera)

        if (vec.z > 1) {
          el.style.visibility = 'hidden'
          el.dataset.markerHidden = 'true'
          continue
        }

        const sx = ((vec.x + 1) / 2) * w
        const sy = ((-vec.y + 1) / 2) * h

        // GPU-beschleunigt positionieren — Offset nur um halbe Dot-Größe (6px),
        // damit der Punkt exakt auf der 3D-Koordinate sitzt (nicht die Wrapper-Mitte).
        el.style.transform = `translate3d(${sx - 6}px, ${sy}px, 0) translate(0, -50%)`
        el.style.visibility = 'visible'
        el.dataset.markerHidden = 'false'

        // Occlusion-Opacity auf den gesamten Marker (Dot + Label) anwenden.
        // Geprüft wird nur der 3D-Ankerpunkt — nicht ob das Label verdeckt ist.
        el.style.opacity = occluded.has(ann.id) ? OCCLUDED_OPACITY : VISIBLE_OPACITY
      }

      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)

    return () => {
      running = false
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [viewerRef, containerRef])

  // ─── Occlusion-Check: periodisch per Potree.Utils.getMousePointCloudIntersection ───
  useEffect(() => {
    const intervalId = setInterval(() => {
      const viewer = viewerRef.current
      const Potree = (window as any).Potree
      const THREE = Potree?.THREE
      const pickFn = Potree?.Utils?.getMousePointCloudIntersection
      if (!viewer || !THREE || !pickFn) return
      if (!viewer.renderer?.domElement || !viewer.scene?.getActiveCamera) return

      const camera = viewer.scene.getActiveCamera()
      if (!camera) return

      const pointclouds = viewer.scene.pointclouds
      if (!pointclouds?.length) return

      const canvas: HTMLCanvasElement = viewer.renderer.domElement
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      const camPos = camera.position

      const anns = annotationsRef.current
      const cloudId = cloudIdRef.current
      const next = new Set<string>()

      for (const ann of anns) {
        if (cloudId && ann.cloudId !== cloudId) continue

        // Projektion wiederholen (leicht, nur Vector3-Mathe)
        const vec = new THREE.Vector3(ann.position.x, ann.position.y, ann.position.z)
        vec.project(camera)
        if (vec.z > 1) continue // hinter Kamera → irrelevant

        const sx = ((vec.x + 1) / 2) * w
        const sy = ((-vec.y + 1) / 2) * h

        // Außerhalb des Viewports → kein Check nötig
        if (sx < 0 || sx > w || sy < 0 || sy > h) continue

        // Distanz Kamera → Marker
        const annVec = new THREE.Vector3(ann.position.x, ann.position.y, ann.position.z)
        const markerDist = camPos.distanceTo(annVec)

        try {
          // Potree GPU-Pick: erwartet canvas-relative {x,y} Pixel-Koordinaten
          // Rückgabe: { location: THREE.Vector3, distance: number, pointcloud, point }
          const mouseV2 = { x: sx, y: sy }
          const pickResult = pickFn(mouseV2, camera, viewer, pointclouds)
          if (pickResult?.location) {
            const pickDist = camPos.distanceTo(pickResult.location)
            // Wenn die getroffene Geometrie deutlich näher liegt als der Marker → verdeckt
            if (pickDist < markerDist * DEPTH_TOLERANCE) {
              next.add(ann.id)
            }
          }
        } catch {
          // Pick nicht verfügbar oder Fehler → nicht verdecken
        }
      }

      occludedRef.current = next
    }, OCCLUSION_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [viewerRef])

  // Cache bei Annotation-Änderungen oder DOM-Mutations neu aufbauen
  useEffect(() => {
    const timer = setTimeout(rebuildCache, 20)
    return () => clearTimeout(timer)
  }, [annotations, currentCloudId, rebuildCache])

  // MutationObserver: reagiert auf DOM-Änderungen (Marker hinzugefügt/entfernt)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new MutationObserver(() => {
      rebuildCache()
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [containerRef, rebuildCache])
}
